import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _without from "lodash/without";
var _excluded = ["alignCheckboxTop", "className", "defaultValue", "disabled", "inline", "name", "onChange", "options", "value", "label", "helpMessage", "error", "errorMessage", "tooltip", "tooltipPlacement"];
import "core-js/modules/es6.function.name.js";
import "core-js/modules/es6.array.map.js";
import React, { useMemo } from 'react';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useId } from '@nerdwallet/react-accessibility';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { InputGroup, shouldRenderInputGroup } from '../InputGroup/InputGroup';
import { CheckboxOption } from './CheckboxOption';
/**
 * Determine if an option is checked given the option's value and the current
 * group value.
 */

var isChecked = function isChecked(currentGroupValue, optionValue) {
  if (currentGroupValue == null) {
    return undefined;
  }

  if (Array.isArray(currentGroupValue)) {
    return currentGroupValue.indexOf(optionValue) !== -1;
  }

  return currentGroupValue === optionValue;
};
/**
 * Add a new selected checkbox value to an group value. Group values can be
 * represented by arrays (because multiple checkboxes can be selected a time)
 * or by a single value (for convenience).
 */


var appendValue = function appendValue(currentGroupValue, optionValue) {
  if (isChecked(currentGroupValue, optionValue)) {
    return currentGroupValue;
  }

  if (currentGroupValue == null) {
    return [optionValue];
  }

  if (Array.isArray(currentGroupValue)) {
    return [].concat(_toConsumableArray(currentGroupValue), [optionValue]);
  }

  return [currentGroupValue, optionValue];
};
/**
 * Add a new selected checkbox value to an group value. Group values can be
 * represented by arrays (because multiple checkboxes can be selected a time)
 * or by a single value (for convenience).
 */


var removeValue = function removeValue(currentGroupValue, optionValue) {
  if (!isChecked(currentGroupValue, optionValue)) {
    return currentGroupValue;
  }

  if (Array.isArray(currentGroupValue)) {
    return _without(currentGroupValue, optionValue);
  }

  return [];
};
/**
 * Util that determines whether the CheckboxOption is disabled, based on the
 * precedence of
 *
 * 1) if disabled is supplied at the top level of the CheckboxGroup, else
 * 2) if the individual CheckboxOption is marked as disabeld, through options.disabled
 */


var isDisabled = function isDisabled(disabledGroup, disabledOption) {
  return disabledGroup || disabledOption;
};
/**
 * Checkboxes allow users to select several options that influence or change their experience.
 */


export var CheckboxGroup = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var alignCheckboxTop = props.alignCheckboxTop,
      className = props.className,
      defaultValue = props.defaultValue,
      disabled = props.disabled,
      inline = props.inline,
      nameProp = props.name,
      onChange = props.onChange,
      options = props.options,
      value = props.value,
      label = props.label,
      helpMessage = props.helpMessage,
      error = props.error,
      errorMessage = props.errorMessage,
      tooltip = props.tooltip,
      tooltipPlacement = props.tooltipPlacement,
      otherProps = _objectWithoutProperties(props, _excluded);

  useDeprecatedClassNameStylePropsWarning('CheckboxGroup', {
    className: className,
    style: otherProps.style
  }); // `name` is used to link related radio buttons together for keyboard
  // navigation. If the name isn't provided, we can use `useId()` to generate
  // one, since it doesn't really matter what the name is as long as it's a
  // globally-unique string.

  var name = useId(nameProp);
  var id = useId();
  var errorMessageId = "".concat(id, "-error-message");
  var helpMessageId = "".concat(id, "-help-message");
  var ariaDescribedByIds = "".concat(errorMessageId, " ").concat(helpMessageId); // We can only attach an `aria-describedby` attribute if at least one of
  // the things it references is actually being displayed.
  // See https://nerdwallet.atlassian.net/browse/FEI-2206

  var renderAriaDescribedBy = helpMessage || errorMessage && error;
  var changeHandlers = useMemo(function () {
    var newChangeHandlers = {};
    options.forEach(function (option) {
      newChangeHandlers[option.value] = function (event) {
        event.stopPropagation();
        onChange({
          target: {
            value: event.target.checked ? appendValue(value, option.value) : removeValue(value, option.value)
          }
        });
      };
    });
    return newChangeHandlers;
  }, [options, value, onChange]);
  var willRenderInputGroup = shouldRenderInputGroup(props);
  var marginClassName = inline ? 'margin-right-3' : 'margin-bottom-2';
  var group = /*#__PURE__*/React.createElement("div", _extends({
    className: classNames(_defineProperty({}, className, className && !willRenderInputGroup)),
    ref: ref
  }, otherProps), options.map(function (option, i) {
    return /*#__PURE__*/React.createElement(CheckboxOption, {
      alignCheckboxTop: alignCheckboxTop,
      "aria-describedby": renderAriaDescribedBy ? ariaDescribedByIds : undefined,
      checked: isChecked(value, option.value),
      className: classNames(_defineProperty({}, classes(marginClassName), i !== options.length - 1)),
      "data-currency": "CheckboxGroup",
      defaultChecked: isChecked(defaultValue, option.value),
      disabled: isDisabled(disabled, option.disabled),
      inline: inline,
      key: option.value,
      label: option.label,
      name: name,
      onChange: changeHandlers[option.value]
    });
  }));

  if (!willRenderInputGroup) {
    return group;
  }

  return /*#__PURE__*/React.createElement(InputGroup, {
    className: className,
    disabled: disabled,
    error: error,
    errorMessage: errorMessage,
    errorMessageId: errorMessageId,
    fieldset: true,
    helpMessage: helpMessage,
    helpMessageId: helpMessageId,
    input: group,
    label: label,
    tooltip: tooltip,
    tooltipPlacement: tooltipPlacement
  });
});
CheckboxGroup.displayName = 'CheckboxGroup'; // Hint to InputGroup and other consumers that a <fieldset> should be used.

CheckboxGroup.shouldUseFieldset = true;

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  CheckboxGroup.propTypes = {
    /** A list of checkbox options (value, label, and if disabled or not). */
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool
    })).isRequired,

    /** The currently selected value(s). */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

    /** Callback to be called when the selected value(s) change. */
    onChange: PropTypes.func,

    /**
     * Whether options should be rendered inline on one line (block by
     * default). This should only be used when there are only two simple
     * options (like "Yes" and "No").
     */
    inline: PropTypes.bool,

    /** Name attribute for <input> elements. */
    name: PropTypes.string,

    /** Initial value when state is uncontrolled. */
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

    /** Flag to indicate if the entire checkbox group should be selectable */
    disabled: PropTypes.bool,

    /** Show the checkbox group in an error visual state. */
    error: PropTypes.bool,

    /** Optional label for the checkbox group. */
    label: PropTypes.string,

    /** Boolean flag to top-align the checkbox when the checkbox text spans multiple lines. */
    alignCheckboxTop: PropTypes.bool,

    /** Optional text to be shown indicating the user is in an error state. */
    errorMessage: PropTypes.node,

    /** Optional help text shown below the checkbox group. */
    helpMessage: PropTypes.node,

    /** Optional tooltip shown via an icon next to the label. */
    tooltip: PropTypes.node,

    /** Positioning of tooltip. */
    tooltipPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

    /**
     * Classes to add to the containing element.
     *
     * @deprecated
     */
    className: PropTypes.string
  };
}

CheckboxGroup.defaultProps = {
  alignCheckboxTop: false,
  className: null,
  defaultValue: null,
  disabled: false,
  error: null,
  errorMessage: null,
  helpMessage: null,
  inline: false,
  label: null,
  name: null,
  onChange: function onChange(f) {
    return f;
  },
  tooltip: null,
  tooltipPlacement: null,
  value: null
};